import * as _ from 'lodash';
import { useState } from 'react';
import { Alert, Checkbox, Glyphicon, Label, Panel, ListGroup, PanelGroup } from 'react-bootstrap';
import { IEmailFlag, IEmailFlagGroup, IEmailFlagValue } from '@nkr/models/index';
import { OrganType } from '@nkr/enums/organ-type';
import { useAppHookContext } from '@nkr/utils/rest-api';
import EmailOptionItem from './email-option-item';

interface IProps {
    data: IEmailFlagValue[],
    emailFlags: IEmailFlag[],
    emailFlagGroups: IEmailFlagGroup[],
    onChange: (list: IEmailFlagValue[]) => void
}

const EmailOptions = (props: IProps) => {
    const { app: { site } } = useAppHookContext();
    const [selectedEmailOption, setSelectedEmailOption] = useState(-1);

    const handlePanelSelected = (activeKey: any) => {
        setSelectedEmailOption(parseInt(activeKey));
    }

    const isEnabled = (flagId: number) => (props.data.find(x => x.flag == flagId)?.value ?? 0) > 0;

    const setFlags = (flags: IEmailFlag[], enable: boolean) => {
        let emailFlags = _.cloneDeep(props.data);

        flags.forEach(flag => {
            let f = emailFlags.find(x => x.flag == flag.id);

            if (f != null) {
                f.value = enable ? 1 : 0;
            } else {
                emailFlags.push({ flag: flag.id, value: enable ? 1 : 0 });
            }
        });

        props.onChange(emailFlags);
    }

    const handleChangeEmailFlag = (flagId: number, value: number) => {
        let emailFlags = _.cloneDeep(props.data);
        let flag = emailFlags.find(x => x.flag == flagId);

        if (flag != null) {
            flag.value = value;
        } else {
            emailFlags.push({ flag: flagId, value });
        }

        props.onChange(emailFlags);
    }

    const allFlagsEnabled = props.emailFlags.length > 0 && props.emailFlags.every(x => isEnabled(x.id));
    const noFlagsEnabled = props.emailFlags.every(x => !isEnabled(x.id));

    const renderGroupStatus = (flags: IEmailFlag[]) => {
        const enabledCount = flags.filter(x => isEnabled(x.id)).length;

        if (enabledCount == flags.length) {
            return <Label bsStyle='success'>All enabled</Label>;
        }

        if (enabledCount == 0) {
            return <Label bsStyle='default'>None enabled</Label>;
        }

        return <Label bsStyle='warning'>{enabledCount} of {flags.length} enabled</Label>;
    }

    return (
        <Panel>
            <Panel.Heading>Receive Email Options</Panel.Heading>
            <Panel.Body>
                {site.organType == OrganType.Kidney && (
                    <Alert bsStyle='info' style={{ marginBottom: 10 }}>
                        <Glyphicon glyph='info-sign' />&nbsp;
                        <strong>All email options are enabled by default.</strong> Uncheck any emails you do not want to receive.
                    </Alert>
                )}

                <div style={{ fontSize: 12, marginBottom: 10 }}>
                    <a style={{ cursor: 'pointer', fontWeight: allFlagsEnabled ? 'bold' : 'normal' }} onClick={() => setFlags(props.emailFlags, true)}>Select All</a>
                    &nbsp;|&nbsp;
                    <a style={{ cursor: 'pointer', fontWeight: noFlagsEnabled ? 'bold' : 'normal' }} onClick={() => setFlags(props.emailFlags, false)}>Clear All</a>
                </div>

                <PanelGroup accordion id='email-options' style={{ marginBottom: 0 }} activeKey={selectedEmailOption} onSelect={handlePanelSelected}>
                    {props.emailFlagGroups.map((group, groupIndex) => {
                        const flags = props.emailFlags.filter(x => x.groupId == group.id);
                        const groupEnabled = flags.length > 0 && flags.every(x => isEnabled(x.id));

                        return (
                            <Panel key={groupIndex} eventKey={groupIndex}>
                                <Panel.Heading style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 7.5px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <Checkbox
                                            checked={groupEnabled}
                                            onChange={() => setFlags(flags, !groupEnabled)}
                                            style={{ margin: '0 8px 0 0' }}
                                            title={groupEnabled ? `Disable all ${group.groupName} emails` : `Enable all ${group.groupName} emails`}
                                        />
                                        <Panel.Title style={{ fontSize: 12 }} toggle>{group.groupName}</Panel.Title>
                                    </div>
                                    {renderGroupStatus(flags)}
                                </Panel.Heading>
                                <Panel.Collapse>
                                    <ListGroup>
                                        {flags.map((flag, flagIndex) => (
                                            <EmailOptionItem
                                                key={`email-option-${groupIndex}-${flagIndex}`}
                                                groupIndex={groupIndex}
                                                itemIndex={flagIndex}
                                                flag={flag}
                                                value={isEnabled(flag.id) ? 1 : 0}
                                                onChange={handleChangeEmailFlag}
                                            />
                                        ))}
                                    </ListGroup>
                                </Panel.Collapse>
                            </Panel>
                        );
                    })}
                </PanelGroup>
            </Panel.Body>
        </Panel>
    );
}

export default EmailOptions;